/**
 * @fileoverview Offline Sync Hook for Tech App
 *
 * Provides:
 * - Online/offline status detection
 * - Automatic sync of pending operations when online
 * - Queue management for offline operations
 *
 * @module hooks/use-offline-sync
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useCurrentTenant } from './use-tenant';
import { toast } from 'sonner';
import {
  getPendingOperations,
  getPendingOperationsCount,
  removeOperation,
  incrementRetryCount,
  queueOperation,
  cacheJobs,
  getCachedJobs,
  updateCachedJob,
  type PendingOperation,
} from '@/lib/offline-storage';
import { JOBS_QUERY_KEY } from './use-jobs';

// ============================================================================
// Types
// ============================================================================

export interface OfflineSyncState {
  isOnline: boolean;
  isSyncing: boolean;
  pendingCount: number;
  lastSyncAt: string | null;
}

// ============================================================================
// Hook: useOfflineSync
// ============================================================================

/**
 * Hook for managing offline sync operations.
 *
 * @example
 * ```tsx
 * function TechApp() {
 *   const { isOnline, pendingCount, syncNow } = useOfflineSync();
 *
 *   return (
 *     <div>
 *       {!isOnline && <OfflineBanner pending={pendingCount} />}
 *       <button onClick={syncNow}>Sync Now</button>
 *     </div>
 *   );
 * }
 * ```
 */
export function useOfflineSync() {
  const { data: tenant } = useCurrentTenant();
  const queryClient = useQueryClient();

  const [state, setState] = useState<OfflineSyncState>({
    isOnline: navigator.onLine,
    isSyncing: false,
    pendingCount: 0,
    lastSyncAt: null,
  });

  const syncInProgressRef = useRef(false);

  // Update pending count
  const updatePendingCount = useCallback(async () => {
    try {
      const count = await getPendingOperationsCount();
      setState(prev => ({ ...prev, pendingCount: count }));
    } catch (error) {
      if (import.meta.env.DEV) console.error('Failed to get pending count:', error);
    }
  }, []);

  // Process a single pending operation
  const processOperation = useCallback(async (operation: PendingOperation): Promise<boolean> => {
    if (!tenant?.id || !supabase) return false;

    try {
      switch (operation.type) {
        case 'job_status_update': {
          const { jobId, status, ...rest } = operation.data as {
            jobId: string;
            status: string;
            [key: string]: unknown;
          };

          const { error } = await supabase
            .from('jobs')
            .update({ status, ...rest })
            .eq('id', jobId)
            .eq('tenant_id', tenant.id);

          if (error) throw error;
          return true;
        }

        case 'survey_submit': {
          const { entries } = operation.data as { entries: Record<string, unknown>[] };

          for (const entry of entries) {
            const { error } = await supabase
              .from('equipment_observations')
              .insert({ ...entry, tenant_id: tenant.id });

            if (error) throw error;
          }
          return true;
        }

        case 'photo_upload': {
          const { jobId: photoJobId, fileName, base64Data } = operation.data as {
            jobId: string;
            fileName: string;
            base64Data: string;
          };

          // Convert base64 back to blob for upload
          const response = await fetch(base64Data);
          const blob = await response.blob();

          // Upload to Supabase Storage
          const filePath = `${tenant.id}/${photoJobId}/${Date.now()}_${fileName}`;
          const { error: uploadError } = await supabase.storage
            .from('job-photos')
            .upload(filePath, blob, { contentType: blob.type });

          if (uploadError) throw uploadError;
          return true;
        }

        default:
          if (import.meta.env.DEV) console.warn('Unknown operation type:', operation.type);
          return false;
      }
    } catch (error) {
      if (import.meta.env.DEV) console.error(`Failed to process operation ${operation.id}:`, error);
      return false;
    }
  }, [tenant?.id]);

  // Sync all pending operations
  const syncPendingOperations = useCallback(async () => {
    if (syncInProgressRef.current || !navigator.onLine) return;

    syncInProgressRef.current = true;
    setState(prev => ({ ...prev, isSyncing: true }));

    try {
      const operations = await getPendingOperations();

      if (operations.length === 0) {
        setState(prev => ({
          ...prev,
          isSyncing: false,
          lastSyncAt: new Date().toISOString(),
        }));
        return;
      }

      let successCount = 0;
      let failCount = 0;

      for (const operation of operations) {
        // Skip operations that have failed too many times
        if (operation.retryCount >= 3) {
          if (import.meta.env.DEV) console.warn(`Skipping operation ${operation.id} - max retries exceeded`);
          failCount++;
          continue;
        }

        const success = await processOperation(operation);

        if (success) {
          await removeOperation(operation.id);
          successCount++;
        } else {
          await incrementRetryCount(operation.id);
          failCount++;
        }
      }

      // Invalidate queries to refresh data
      queryClient.invalidateQueries({ queryKey: JOBS_QUERY_KEY });

      // Show sync result
      if (successCount > 0) {
        toast.success(`Synced ${successCount} pending operation${successCount > 1 ? 's' : ''}`);
      }
      if (failCount > 0) {
        toast.warning(`${failCount} operation${failCount > 1 ? 's' : ''} failed to sync`);
      }

      await updatePendingCount();
      setState(prev => ({
        ...prev,
        isSyncing: false,
        lastSyncAt: new Date().toISOString(),
      }));
    } catch (error) {
      if (import.meta.env.DEV) console.error('Sync failed:', error);
      setState(prev => ({ ...prev, isSyncing: false }));
    } finally {
      syncInProgressRef.current = false;
    }
  }, [processOperation, queryClient, updatePendingCount]);

  // Queue a job status update for offline processing
  const queueJobStatusUpdate = useCallback(async (
    jobId: string,
    status: string,
    additionalData?: Record<string, unknown>
  ) => {
    // Update local cache immediately (optimistic update)
    await updateCachedJob(jobId, { status, ...additionalData });

    // If online, try to sync immediately
    if (navigator.onLine && supabase && tenant?.id) {
      try {
        const { error } = await supabase
          .from('jobs')
          .update({ status, ...additionalData })
          .eq('id', jobId)
          .eq('tenant_id', tenant.id);

        if (!error) {
          queryClient.invalidateQueries({ queryKey: JOBS_QUERY_KEY });
          return true;
        }
      } catch {
        // Fall through to queue
      }
    }

    // Queue for later sync
    await queueOperation('job_status_update', {
      jobId,
      status,
      ...additionalData,
    });
    await updatePendingCount();
    toast.info('Update saved offline. Will sync when connected.');
    return false;
  }, [tenant?.id, queryClient, updatePendingCount]);

  // Queue survey submission for offline processing
  const queueSurveySubmit = useCallback(async (entries: Record<string, unknown>[]) => {
    await queueOperation('survey_submit', { entries });
    await updatePendingCount();
    toast.info('Survey saved locally. Will sync when connected.');
  }, [updatePendingCount]);

  // Cache jobs data for offline access
  const cacheJobsData = useCallback(async (jobs: Record<string, unknown>[]) => {
    try {
      await cacheJobs(jobs);
    } catch (error) {
      if (import.meta.env.DEV) console.error('Failed to cache jobs:', error);
    }
  }, []);

  // Get cached jobs when offline
  const getCachedJobsData = useCallback(async () => {
    try {
      return await getCachedJobs();
    } catch (error) {
      if (import.meta.env.DEV) console.error('Failed to get cached jobs:', error);
      return [];
    }
  }, []);

  // Manual sync trigger
  const syncNow = useCallback(() => {
    if (navigator.onLine) {
      syncPendingOperations();
    } else {
      toast.error("Can't sync while offline");
    }
  }, [syncPendingOperations]);

  // Setup online/offline listeners
  useEffect(() => {
    const handleOnline = () => {
      setState(prev => ({ ...prev, isOnline: true }));
      // Auto-sync when coming back online
      syncPendingOperations();
    };

    const handleOffline = () => {
      setState(prev => ({ ...prev, isOnline: false }));
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Initial pending count
    updatePendingCount();

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [syncPendingOperations, updatePendingCount]);

  return {
    ...state,
    syncNow,
    queueJobStatusUpdate,
    queueSurveySubmit,
    cacheJobsData,
    getCachedJobsData,
  };
}

// ============================================================================
// Hook: useOnlineStatus
// ============================================================================

/**
 * Simple hook for online/offline status only.
 *
 * @example
 * ```tsx
 * function StatusIndicator() {
 *   const isOnline = useOnlineStatus();
 *   return <Badge>{isOnline ? 'Online' : 'Offline'}</Badge>;
 * }
 * ```
 */
export function useOnlineStatus(): boolean {
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  return isOnline;
}
